import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');
    const oldCount = this.#cartBadgeText();
    // Hold the bag's old number until the flying logo lands.
    if (this.dataset.flyLogo) this.#holdBadge(oldCount);

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#releaseBadge();
        this.#showError(errorMessage);
      } else if (this.dataset.flyLogo && this.#cartBadgeTarget()) {
        // Brand product pages: the brand logo flies into the bag instead of opening the cart drawer.
        this.#flyToCart(submitter, oldCount);
      } else {
        this.#releaseBadge();
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#releaseBadge();
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  // === Add-to-cart logo flight (brand product pages, opt-in via data-fly-logo) ===

  #cartTrigger() {
    return document.getElementById('header-cart-trigger');
  }

  #cartBadgeTarget() {
    const icon = this.#cartTrigger()?.querySelector('span[aria-hidden="true"]');
    if (!icon) return null;
    const rect = icon.getBoundingClientRect();
    return rect.width > 0 ? icon : null;
  }

  #cartBadgeText() {
    return this.#cartTrigger()?.querySelector('.rounded-full')?.textContent.trim() || '';
  }

  #holdBadge(oldCount) {
    this.#releaseBadge();
    const icon = this.#cartBadgeTarget();
    if (!icon) return;
    // A stand-in badge keeps the old number showing while the real one is hidden during the flight.
    const style = document.createElement('style');
    style.dataset.cartFlyHold = '';
    style.textContent = '#header-cart-trigger .rounded-full { visibility: hidden; }';
    document.head.append(style);
    if (!oldCount) return;
    const rect = icon.getBoundingClientRect();
    const ghost = document.createElement('span');
    ghost.dataset.cartFlyGhost = '';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.className = 'flex items-center justify-center size-5 text-xs font-medium bg-interactive text-interactive-text rounded-full pointer-events-none';
    ghost.textContent = oldCount;
    Object.assign(ghost.style, { position: 'fixed', zIndex: '2147483646' });
    ghost.style.left = `${rect.right - 10}px`;
    ghost.style.top = `${rect.top - 10}px`;
    document.body.append(ghost);
  }

  #releaseBadge() {
    document.querySelectorAll('[data-cart-fly-hold], [data-cart-fly-ghost]').forEach((node) => node.remove());
  }

  async #waitForNewCount(oldCount) {
    // The header count refreshes on its own after the add; give it a moment to arrive.
    for (let i = 0; i < 20; i += 1) {
      const text = this.#cartBadgeText();
      if (text && text !== oldCount) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async #flyToCart(submitter, oldCount) {
    const icon = this.#cartBadgeTarget();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduce && icon) {
      const from = (submitter instanceof HTMLElement ? submitter : this).getBoundingClientRect();
      const to = icon.getBoundingClientRect();
      const startX = from.left + from.width / 2;
      const startY = from.top + from.height / 2;
      const endX = to.left + to.width / 2;
      const endY = to.top + to.height / 2;
      const size = 96;

      const logo = document.createElement('img');
      logo.src = this.dataset.flyLogo;
      logo.alt = '';
      logo.setAttribute('aria-hidden', 'true');
      Object.assign(logo.style, {
        position: 'fixed',
        left: `${startX - size / 2}px`,
        top: `${startY - size / 2}px`,
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'contain',
        pointerEvents: 'none',
        zIndex: '2147483647',
      });
      document.body.append(logo);

      const dx = endX - startX;
      const dy = endY - startY;
      // One continuous curve (quadratic Bézier) with smooth easing, so there's no corner or speed jump mid-flight.
      const lift = Math.min(160, Math.abs(dx) * 0.35 + 60);
      const cx = dx * 0.5;
      const cy = Math.min(0, dy) - lift;
      const duration = 800;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
      logo.style.willChange = 'transform, opacity';
      await new Promise((resolve) => {
        let start = 0;
        const step = (now) => {
          if (!start) start = now;
          const t = Math.min(1, (now - start) / duration);
          const p = ease(t);
          const inv = 1 - p;
          const x = 2 * inv * p * cx + p * p * dx;
          const y = 2 * inv * p * cy + p * p * dy;
          const scale = 1 - 0.88 * p;
          logo.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
          logo.style.opacity = String(p < 0.75 ? 1 : 1 - ((p - 0.75) / 0.25) * 0.7);
          if (t < 1) requestAnimationFrame(step);
          else resolve();
        };
        requestAnimationFrame(step);
      });
      logo.remove();
    }

    await this.#waitForNewCount(oldCount);
    this.#releaseBadge();
    if (reduce) return;

    // Land: the bag bounces and the new number pops in.
    this.#cartBadgeTarget()?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(0.95)' }, { transform: 'scale(1)' }],
      { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
    this.#cartTrigger()?.querySelector('.rounded-full')?.animate(
      [{ scale: '0.4' }, { scale: '1' }],
      { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
